import { api } from "_endpoints/api";
import http from "../../axios";
import {
  DASHBOARD_ANALYTICS_REQUEST,
  DASHBOARD_ANALYTICS_FAILURE,
  DASHBOARD_ANALYTICS_SUCCESS,
  FETCH_MY_JOBS_REQUEST,
  FETCH_MY_JOBS_SUCCESS,
  FETCH_MY_JOBS_FAIL,
  FETCH_ARCHIVED_JOBS_REQUEST,
  FETCH_ARCHIVED_JOBS_SUCCESS,
  FETCH_ARCHIVED_JOBS_FAIL,
  UPDATE_ACTIVITY_VIEW_REQUEST,
  UPDATE_ACTIVITY_VIEW_SUCCESS,
  UPDATE_ACTIVITY_VIEW_FAIL,
  FETCH_DROPDOWN_DATA_FAIL,
  FETCH_DROPDOWN_DATA_REQUEST,
  FETCH_DROPDOWN_DATA_SUCCESS,
  FETCH_LANDLORDS_SUCCESS,
  FETCH_LISTINGS_SUCCESS,
  SET_ARCHIVED_MONTHS,
  FETCH_TRADESPERSONS_SUCCESS,
  FETCH_PRIMARY_CONTACTS_SUCCESS,
  FETCH_TRADESPERSON_LISTINGS_SUCCESS
} from "./types";
import { setError } from "redux/errors/errorActions";
import { Roles } from "_enums/enums";
import { pillarOf } from "_shared/jobType";
import { isClosedJobsView } from "_shared/myJobsView";
import { viewAllTradesmans, getLandlords } from "_services/tradesman-service";
import { getListings } from "_services/listing-service";

export const getAnalytics = () => {
  return async (dispatch, getState) => {
    try {
      const { filterTags } = getState().tag;
      dispatch({ type: DASHBOARD_ANALYTICS_REQUEST });
      const response = await http.post(api.dashboard.analytics, { filterTags: filterTags });
      dispatch({ type: DASHBOARD_ANALYTICS_SUCCESS, payload: response.data });
    } catch (error) {
      dispatch({ type: DASHBOARD_ANALYTICS_FAILURE, payload: error?.response?.data?.message });
    }
  };
};

/**
 * The filters as sent to the My Jobs APIs: always a pillar (Repairs when none
 * is picked) and a stage ("" is All).
 */
const myJobsRequestFilters = (filters) => ({ ...filters, jobType: pillarOf(filters), stage: filters?.stage || "" });

/**
 * Landlord-side grouped view (sub-tasks nested under their master) only while
 * nothing is selected: stage All, no drill-down, no panel filter. Anything
 * selected gives a flat list where each sub-task is its own row and shows only
 * if it matches - otherwise a master would bring along sub-tasks that don't.
 */
const isNothingSelected = ({ filters = {}, inProgressClickedData, ageProfileClickedData }) =>
  !filters.stage &&
  !filters.status &&
  !filters.openOnly &&
  !filters.property &&
  !filters.tradesPerson &&
  !filters.landlord &&
  !filters.primaryContact &&
  !filters.complianceRunId &&
  !inProgressClickedData &&
  !ageProfileClickedData;

export const getJobs = (paginationOptions = {}) => {
  return async (dispatch, getState) => {
    try {
      const { filterTags } = getState().tag;
      const { filters, inProgressClickedData, ageProfileClickedData } = getState().dashboard;

      const role = getState().user?.userData?.role;
      const jobsView =
        [Roles.Landlord, Roles.Liaison].includes(role) && isNothingSelected(getState().dashboard)
          ? "grouped"
          : "flat";

      // Build request payload with pagination and filters
      const payload = {
        filterTags: filterTags,
        page: paginationOptions.page !== undefined ? paginationOptions.page : 0,
        limit: paginationOptions.limit !== undefined ? paginationOptions.limit : 25,
        filters: myJobsRequestFilters(filters),
        sortBy: paginationOptions.sortBy || "createdAt",
        sortOrder: paginationOptions.sortOrder || "desc",
        jobsView,
        // Pass dashboard filters if they exist
        ...(inProgressClickedData && { inProgressClickedData }),
        ...(ageProfileClickedData && { ageProfileClickedData })
      };

      dispatch({ type: FETCH_MY_JOBS_REQUEST });
      const response = await http.post(api.tickets.getAllTickets, payload);
      dispatch({ type: FETCH_MY_JOBS_SUCCESS, payload: response.data });
    } catch (error) {
      dispatch({ type: FETCH_MY_JOBS_FAIL, payload: error.response?.data?.message });
    }
  };
};

export const getArchivedJobs = (paginationOptions = {}) => {
  return async (dispatch, getState) => {
    try {
      const { filterTags } = getState().tag;
      const { filters, archivedData, inProgressClickedData, ageProfileClickedData } = getState().dashboard;

      // Get months from payload or existing state
      const months = paginationOptions.months !== undefined ? paginationOptions.months : archivedData.months;
      // The Filters panel's closed-jobs period replaces months.
      const closedView = isClosedJobsView(filters);

      if (!closedView && (months ?? 0) === 0) {
        dispatch(setError([{ msg: "date not set", isOpen: true, duration: 6000, severity: "warning" }]));
        return;
      }

      dispatch({ type: FETCH_ARCHIVED_JOBS_REQUEST });

      // The archive is a selection (closed jobs), so it is always flat.
      const jobsView = "flat";

      // Build request payload with pagination and filters (same structure as getJobs)
      const payload = {
        filterTags: filterTags,
        page: paginationOptions.page !== undefined ? paginationOptions.page : 0,
        limit: paginationOptions.limit !== undefined ? paginationOptions.limit : 25,
        filters: myJobsRequestFilters(filters),
        sortBy: paginationOptions.sortBy || "createdAt",
        sortOrder: paginationOptions.sortOrder || "desc",
        ...(closedView ? { closedPeriod: filters.closedPeriod } : { months }),
        jobsView,
        // Pass dashboard filters if they exist
        ...(inProgressClickedData && { inProgressClickedData }),
        ...(ageProfileClickedData && { ageProfileClickedData })
      };

      const response = await http.post(api.tickets.getArchivedJobs, payload);

      // Handle new format (with pagination) and old format (array) for backward compatibility
      const isNewFormat = response.data && typeof response.data === "object" && !Array.isArray(response.data) && response.data.tickets;

      if (isNewFormat) {
        // The closed-jobs view has its own empty state.
        if (response.data.tickets.length === 0 && !closedView) {
          dispatch(setError([{ msg: "No records found", isOpen: true, duration: 6000, severity: "warning" }]));
        }
        dispatch({
          type: FETCH_ARCHIVED_JOBS_SUCCESS,
          payload: {
            tickets: response.data.tickets,
            pagination: response.data.pagination,
            metadata: response.data.metadata,
            months: months
          }
        });
      } else {
        // Old format (backward compatibility)
        if (response.data.length === 0) {
          dispatch(setError([{ msg: "No records found", isOpen: true, duration: 6000, severity: "warning" }]));
        }
        dispatch({
          type: FETCH_ARCHIVED_JOBS_SUCCESS,
          payload: {
            tickets: response.data,
            months: months
          }
        });
      }
    } catch (error) {
      dispatch({ type: FETCH_ARCHIVED_JOBS_FAIL, payload: error.response?.data?.message });
    }
  };
};

// Cache configuration - 5 minutes cache duration (for Redux state cache only)
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Action to set archived months without making API call (let useEffect handle the API call)
export const setArchivedMonths = (months) => {
  return {
    type: SET_ARCHIVED_MONTHS,
    payload: months
  };
};

export const fetchFilterDropdowns = () => {
  return async (dispatch, getState) => {
    const { userData } = getState().user;

    try {
      dispatch({ type: FETCH_DROPDOWN_DATA_REQUEST });

      const requests = [];
      const requestTypes = [];
      const { filterTags } = getState().tag;
      const { FilterDropDown } = getState().dashboard;
      const now = Date.now();

      if (userData.role !== Roles.TradesPerson) {
        // Listings are fetched for tenants too — the endpoint scopes to the caller
        // (a tenant gets the properties they rent), and without this a tenant
        // queued no requests at all, leaving their property filter permanently empty.
        requests.push(getListings({ filterTags, lightweight: true }));
        requestTypes.push("listings");

        if (userData.role === Roles.Tenant) {
          // Tenants get the tradespeople assigned to their own visible tickets.
          // /trades/all is landlord-scoped and would both 403 and expose the
          // landlord's whole contact list.
          requests.push(http.post(api.tickets.getTenantTradespersons, { filterTags }));
          requestTypes.push("tradespersons");
        } else {
          // The remaining two are landlord/liaison-only endpoints; a tenant calling
          // them would 403 and reject the whole Promise.all.

          // Check Redux cache for trades/all (tradespersons)
          const tradespersonsCacheAge = FilterDropDown.tradespersonsCachedAt ? now - FilterDropDown.tradespersonsCachedAt : CACHE_DURATION_MS + 1;
          if (FilterDropDown.tradesPerson?.length > 0 && tradespersonsCacheAge < CACHE_DURATION_MS) {
            // Cache valid - skip API call
          } else {
            requests.push(viewAllTradesmans());
            requestTypes.push("tradespersons");
          }

          // Check Redux cache for primary contacts (landlords/liaisons only)
          const cacheAge = FilterDropDown.primaryContactCacheTimestamp ? now - FilterDropDown.primaryContactCacheTimestamp : CACHE_DURATION_MS + 1; // Force fetch if no timestamp

          if (FilterDropDown.primaryContact && FilterDropDown.primaryContact.length > 0 && cacheAge < CACHE_DURATION_MS) {
            // Use cached data from Redux - no need to dispatch, it's already in state
            // Skip API call
          } else {
            // Fetch fresh data
            requests.push(http.post(api.tickets.getPrimaryContacts, { filterTags }));
            requestTypes.push("primaryContacts");
          }
        }
      } else {
        // TradesPerson: Check Redux cache for listings (properties) from tickets
        const listingsCacheAge = FilterDropDown.tradespersonListingsCacheTimestamp
          ? now - FilterDropDown.tradespersonListingsCacheTimestamp
          : CACHE_DURATION_MS + 1; // Force fetch if no timestamp

        if (FilterDropDown.property && FilterDropDown.property.length > 0 && listingsCacheAge < CACHE_DURATION_MS) {
          // Cache is valid - data already in Redux state, no need to fetch or dispatch
          // Skip API call
        } else {
          // Cache expired or missing - fetch fresh data
          requests.push(http.post(api.tickets.getTradespersonListings, { filterTags }));
          requestTypes.push("tradespersonListings");
        }

        // Always fetch fresh landlords data
        requests.push(getLandlords());
        requestTypes.push("landlords");
      }

      // Fetch only uncached data
      if (requests.length > 0) {
        const responses = await Promise.all(requests);

        let responseIndex = 0;
        requestTypes.forEach((type) => {
          if (type === "listings") {
            const listingsRes = responses[responseIndex++];
            const listingsData = listingsRes.data.listings.map((listing) => ({
              label: `${listing.line1}, ${listing.postcode}`,
              value: listing._id
            }));

            dispatch({ type: FETCH_LISTINGS_SUCCESS, payload: listingsData });
          } else if (type === "tradespersons") {
            const tradesRes = responses[responseIndex++];
            if (tradesRes.data.status) {
              const tradespersonsData = tradesRes.data.result.map((tp) => ({
                // A tradesperson saved without an email has no profileId; without
                // the Trades-level fallback this renders "undefined - undefined".
                label:
                  tp.type === Roles.PropertyOwner
                    ? `Property Owner - ${tp.propertyOwnerId?.name || tp.fullName}`
                    : `${tp?.profileId?.displayRole || tp?.profileId?.role || tp.type} - ${
                        tp.profileId ? `${tp.profileId.firstName} ${tp.profileId.lastName}` : tp.fullName
                      }`,
                value: tp._id
              }));
              dispatch({
                type: FETCH_TRADESPERSONS_SUCCESS,
                payload: { data: tradespersonsData, timestamp: Date.now() }
              });
            }
          } else if (type === "landlords") {
            const landlordsRes = responses[responseIndex++];
            if (landlordsRes.data.status) {
              const landlordsData = landlordsRes.data.result.map((tp) => ({
                label: `${tp.userId.firstName} ${tp.userId.lastName}`,
                value: tp.userId._id
              }));

              dispatch({ type: FETCH_LANDLORDS_SUCCESS, payload: landlordsData });
            }
          } else if (type === "primaryContacts") {
            const primaryContactsRes = responses[responseIndex++];
            if (primaryContactsRes.data.status && primaryContactsRes.data.contacts) {
              const primaryContactsData = primaryContactsRes.data.contacts.map((contact) => ({
                label: contact.name || `${contact.firstName || ""} ${contact.lastName || ""}`.trim(),
                value: contact._id
              }));

              // Store in Redux state with timestamp (no localStorage)
              dispatch({
                type: FETCH_PRIMARY_CONTACTS_SUCCESS,
                payload: {
                  data: primaryContactsData,
                  timestamp: Date.now()
                }
              });
            }
          } else if (type === "tradespersonListings") {
            const listingsRes = responses[responseIndex++];
            if (listingsRes.data.status && listingsRes.data.listings) {
              const listingsData = listingsRes.data.listings.map((listing) => ({
                label: listing.label || `${listing.line1 || ""}, ${listing.postcode || ""}`.trim(),
                value: listing._id
              }));

              // Store in Redux state with timestamp (no localStorage)
              dispatch({
                type: FETCH_TRADESPERSON_LISTINGS_SUCCESS,
                payload: {
                  data: listingsData,
                  timestamp: Date.now()
                }
              });
            }
          }
        });
      }

      // Property, tradesperson, and landlord filters are optional (not required)
      // Validation is always false - these filters are optional for both regular and archived jobs
      dispatch({ type: FETCH_DROPDOWN_DATA_SUCCESS, payload: false });
    } catch (error) {
      console.log(error);
      dispatch({
        type: FETCH_DROPDOWN_DATA_FAIL,
        payload: error.response?.data?.message || error?.message
      });
    }
  };
};

export const updateActivityView = (payload) => {
  return async (dispatch, getState) => {
    try {
      const { filterTags } = getState().tag;
      dispatch({ type: UPDATE_ACTIVITY_VIEW_REQUEST });
      const response = await http.post(api.dashboard.updateActivityView, { filterTags, ...payload });
      dispatch({ type: UPDATE_ACTIVITY_VIEW_SUCCESS, payload: response.data });
    } catch (error) {
      dispatch({ type: UPDATE_ACTIVITY_VIEW_FAIL, payload: error.response?.data?.message });
    }
  };
};
